// src/ctx/providers/AuthContextProvider.tsx
import API, { setUnauthorizedHandler } from "@/config/api";
import {
  type AuthStatus,
  type AuthContextData,
  AuthContext,
} from "@/context/AuthContext";
import useReturnTo from "@/hooks/useReturnTo";
import type { User } from "@/types/user";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type RefreshResponse = {
  user: User;
  message: string;
};

export function AuthContextProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>("loading");
  const [error, setError] = useState<string | null>(null);
  const bootstrapStarted = useRef(false);
  const refreshPromiseRef = useRef<Promise<User | null> | null>(null);
  const { returnTo } = useReturnTo();

  const applyAuthenticatedUser = useCallback((nextUser: User) => {
    setUser(nextUser);
    setAuthStatus("authenticated");
    setError(null);
  }, []);

  const clearSession = useCallback((message?: string) => {
    setUser(null);
    setAuthStatus("unauthenticated");
    setError(message ?? null);
  }, []);

  const refresh = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = API.main
      .post<RefreshResponse>("/auth/refresh")
      .then((response) => {
        const nextUser = response.data?.user;

        if (!nextUser) {
          throw new Error("Session refresh did not return a user.");
        }

        applyAuthenticatedUser(nextUser);
        return nextUser;
      })
      .catch(() => {
        clearSession("Your session has expired. Please sign in again.");
        return null;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    return refreshPromiseRef.current;
  }, [applyAuthenticatedUser, clearSession]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession("Your session has expired. Please sign in again.");
    });

    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    if (bootstrapStarted.current) return;
    bootstrapStarted.current = true;

    void refresh();
  }, [refresh]);

  const login = useCallback(
    (nextUser: User) => {
      applyAuthenticatedUser(nextUser);
    },
    [applyAuthenticatedUser],
  );

  const logout = useCallback<AuthContextData["logout"]>(
    async ({ callServer = true, stayLoggedOut = true } = {}) => {
      try {
        if (callServer) {
          await API.main.post("/auth/logout").catch(() => undefined);
          refresh();
        }
      } finally {
        refresh()
        clearSession("Session cleared");
        if (!stayLoggedOut) returnTo({ replace: true });
      }
    },
    [clearSession, returnTo, refresh],
  );

  const updateUser = useCallback(
    (nextUser: User | null) => {
      if (nextUser) {
        applyAuthenticatedUser(nextUser);
      } else {
        clearSession("Session cleared.");
      }
    },
    [applyAuthenticatedUser, clearSession],
  );

  const value: AuthContextData = useMemo(
    () => ({
      user,
      authStatus,
      loading: authStatus === "loading",
      isAuthenticated: authStatus === "authenticated",
      error,
      setUser: updateUser,
      login,
      logout,
      refresh,
    }),
    [authStatus, error, login, logout, refresh, updateUser, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
